import React from "react";

/**
 * VaultLogo - pure SVG vault door icon:
 * - Circle with globe latitude/longitude grid
 * - Four bolt handles at 45° diagonals
 * - Centre locking knob (subtle spin)
 */
export const VaultLogo = ({ size = 220, animated = true, className = "" }) => {
  const gold = "#C9960C";
  const paleGold = "#F5D485";

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 240 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      data-testid="vault-logo-svg"
      aria-label="The Global Vault logo"
    >
      {/* Outer ring */}
      <circle cx="120" cy="120" r="100" stroke={gold} strokeWidth="2.5" />
      {/* Inner ring */}
      <circle cx="120" cy="120" r="86" stroke={gold} strokeWidth="1" opacity="0.7" />

      {/* Globe latitude (horizontal arcs) */}
      <ellipse cx="120" cy="120" rx="86" ry="32" stroke={paleGold} strokeWidth="0.8" opacity="0.55" />
      <ellipse cx="120" cy="120" rx="86" ry="60" stroke={paleGold} strokeWidth="0.8" opacity="0.4" />
      <line x1="34" y1="120" x2="206" y2="120" stroke={paleGold} strokeWidth="0.8" opacity="0.55" />

      {/* Globe longitude (vertical arcs) */}
      <ellipse cx="120" cy="120" rx="32" ry="86" stroke={paleGold} strokeWidth="0.8" opacity="0.55" />
      <ellipse cx="120" cy="120" rx="60" ry="86" stroke={paleGold} strokeWidth="0.8" opacity="0.4" />
      <line x1="120" y1="34" x2="120" y2="206" stroke={paleGold} strokeWidth="0.8" opacity="0.55" />

      {/* Four bolt handles at 45° diagonals (NE, NW, SE, SW) */}
      {[
        { x1: 162, y1: 78, x2: 198, y2: 42 },   // NE
        { x1: 78, y1: 78, x2: 42, y2: 42 },     // NW
        { x1: 162, y1: 162, x2: 198, y2: 198 }, // SE
        { x1: 78, y1: 162, x2: 42, y2: 198 },   // SW
      ].map((b, idx) => (
        <g key={idx}>
          <line x1={b.x1} y1={b.y1} x2={b.x2} y2={b.y2} stroke={gold} strokeWidth="3" strokeLinecap="square" />
          <circle cx={b.x2} cy={b.y2} r="7" fill={gold} />
          <circle cx={b.x2} cy={b.y2} r="3" fill="#080808" />
          {/* Inner bolt cap */}
          <circle cx={b.x1} cy={b.y1} r="4" fill={gold} />
        </g>
      ))}

      {/* Centre locking knob (stationary) */}
      <g style={{ transformBox: "fill-box", transformOrigin: "120px 120px" }}>
        <circle cx="120" cy="120" r="26" fill="#080808" stroke={gold} strokeWidth="2" />
        <circle cx="120" cy="120" r="18" fill="none" stroke={paleGold} strokeWidth="0.8" />
        {/* Spokes */}
        {[0, 45, 90, 135].map((deg) => (
          <line
            key={deg}
            x1="120"
            y1="98"
            x2="120"
            y2="142"
            stroke={gold}
            strokeWidth="2"
            strokeLinecap="round"
            transform={`rotate(${deg} 120 120)`}
          />
        ))}
        <circle cx="120" cy="120" r="5" fill={gold} />
      </g>
    </svg>
  );
};

export const VaultLogoMini = ({ size = 36, className = "" }) => {
  const gold = "#C9960C";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 240 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      data-testid="vault-logo-mini-svg"
      aria-hidden="true"
    >
      <circle cx="120" cy="120" r="100" stroke={gold} strokeWidth="6" />
      <ellipse cx="120" cy="120" rx="100" ry="40" stroke={gold} strokeWidth="4" opacity="0.65" />
      <ellipse cx="120" cy="120" rx="40" ry="100" stroke={gold} strokeWidth="4" opacity="0.65" />
      {[
        { x1: 162, y1: 78, x2: 198, y2: 42 },
        { x1: 78, y1: 78, x2: 42, y2: 42 },
        { x1: 162, y1: 162, x2: 198, y2: 198 },
        { x1: 78, y1: 162, x2: 42, y2: 198 },
      ].map((b, idx) => (
        <line key={idx} x1={b.x1} y1={b.y1} x2={b.x2} y2={b.y2} stroke={gold} strokeWidth="8" strokeLinecap="square" />
      ))}
      <circle cx="120" cy="120" r="26" fill={gold} />
    </svg>
  );
};
